// pages/secure/OnboardingAdminPage.js
// Onboarding admin: start a new hire's checklist from a template, watch progress,
// and tick steps off — every action happens in pop-ups so the list stays clean.
// The welcome email (with their assigned brokerage address) fires automatically.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

import Modal from '../../components/secure/Modal';
import OnboardingTemplateEditor from '../../components/secure/OnboardingTemplateEditor';

const STEP_BADGE = { done: 'bg-green-100 text-green-700', pending: 'bg-slate-100 text-slate-600', skipped: 'bg-amber-100 text-amber-700' };
const EMPTY_START = { template_id: '', agent_id: '', employee_id: '', personal_email: '', assigned_email: '' };

const OnboardingAdminPage = ({ user }) => {
    const apiClient = useApiClient();
    const [onboardings, setOnboardings] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [agents, setAgents] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [starting, setStarting] = useState(null);   // start-onboarding form
    const [detail, setDetail] = useState(null);       // onboarding w/ steps
    const [skipping, setSkipping] = useState(null);   // {step, reason} — a blocking step being set aside
    const [isSaving, setIsSaving] = useState(false);
    const [flash, setFlash] = useState('');
    const [editingTemplates, setEditingTemplates] = useState(false);

    // Microsoft 365 mailbox provisioning. Managing broker only, and never automatic —
    // it creates a real identity in the tenant and consumes a paid license.
    const [provision, setProvision] = useState(null);       // the form
    const [provisionState, setProvisionState] = useState(null); // {available, reason, skus}
    const [provisionResult, setProvisionResult] = useState(null); // credentials, shown once
    const canProvision = !!user?.isManagingBroker;

    const load = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/onboarding')
            .then(setOnboardings)
            .catch(err => setError(err.message || 'Failed to load onboardings.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const openStart = async () => {
        setError('');
        try {
            const [tpls, ags, emps] = await Promise.all([
                apiClient.get('/api/onboarding/templates'),
                apiClient.get('/api/agents'),
                apiClient.get('/api/employees'),
            ]);
            setTemplates(tpls);
            setAgents(ags);
            setEmployees(emps);
            setStarting({ ...EMPTY_START });
        } catch (err) { setError(err.message); }
    };

    const selectedTemplate = templates.find(t => String(t.template_id) === String(starting?.template_id));

    const handleStart = async () => {
        setIsSaving(true);
        setError('');
        try {
            const payload = {
                template_id: starting.template_id,
                personal_email: starting.personal_email || undefined,
                assigned_email: starting.assigned_email || undefined,
            };
            if (String(selectedTemplate?.audience || '').startsWith('agent')) payload.agent_id = starting.agent_id;
            else payload.employee_id = starting.employee_id;
            const result = await apiClient.post('/api/onboarding', payload);
            setStarting(null);
            setFlash(result.welcome_email_sent ? '✓ Onboarding started — welcome email sent automatically.' : '✓ Onboarding started.');
            setTimeout(() => setFlash(''), 4000);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const openDetail = async (id) => {
        try { setDetail(await apiClient.get(`/api/onboarding/${id}`)); }
        catch (err) { setError(err.message); }
    };

    const setStep = async (step, status, skipped_reason) => {
        // Skipping something that blocks needs a reason. Asking here rather than
        // letting the save come back 422 — the person deciding to skip is the one
        // who knows why, and a minute later they are on another screen.
        if (status === 'skipped' && step.is_blocking && !skipped_reason) {
            setSkipping({ step, reason: '' });
            return;
        }
        setError('');
        try {
            await apiClient.put(`/api/onboarding/steps/${step.onboarding_step_id}`,
                { status, ...(skipped_reason ? { skipped_reason } : {}) });
            const refreshed = await apiClient.get(`/api/onboarding/${detail.onboarding_id}`);
            setDetail(refreshed);
            setSkipping(null);
            load();
        } catch (err) { setError(err.message); }
    };

    const openProvision = async () => {
        setError('');
        setProvisionResult(null);
        // Suggest an address from their name, but never assume it — the broker confirms.
        const suggested = String(detail.person_name || '').trim().toLowerCase()
            .replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean).join('.');
        setProvision({
            userPrincipalName: detail.assigned_email || (suggested ? `${suggested}@businessrealestatecorp.com` : ''),
            skuId: '',
        });
        try {
            setProvisionState(await apiClient.get('/api/onboarding/provisioning/status'));
        } catch (err) {
            setProvisionState({ available: false, reason: err.message, skus: [] });
        }
    };

    const submitProvision = async () => {
        const upn = provision.userPrincipalName.trim();
        if (!window.confirm(
            `Create a Microsoft 365 account for ${detail.person_name} at ${upn}?\n\n`
            + 'This creates a real account in the tenant and, if a license is selected, consumes a paid seat.'
        )) return;
        setIsSaving(true);
        setError('');
        try {
            const result = await apiClient.post(`/api/onboarding/${detail.onboarding_id}/provision-mailbox`, {
                userPrincipalName: upn,
                skuId: provision.skuId || undefined,
            });
            setProvision(null);
            setProvisionResult(result);
            setDetail(await apiClient.get(`/api/onboarding/${detail.onboarding_id}`));
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const cancelOnboarding = async () => {
        if (!window.confirm(`Cancel the onboarding for ${detail.person_name}?`)) return;
        try {
            await apiClient.put(`/api/onboarding/${detail.onboarding_id}`, { status: 'cancelled' });
            setDetail(null);
            load();
        } catch (err) { setError(err.message); }
    };

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Onboarding</h1>
                <div className="flex gap-2">
                    {user?.isManagingBroker && (
                        <button onClick={() => setEditingTemplates(true)} className="border border-slate-300 px-4 py-2 rounded-md hover:bg-slate-50">Edit templates</button>
                    )}
                    <button onClick={openStart} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ Start onboarding</button>
                </div>
            </div>
            <OnboardingTemplateEditor open={editingTemplates} onClose={() => setEditingTemplates(false)} />
            <p className="text-slate-500 mb-6">Every new agent and employee, from sponsor card to full desk — with the Illinois requirements built into the checklist.</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}
            {flash && <p className="mb-4 text-green-600">{flash}</p>}

            <div className="bg-white rounded-lg shadow divide-y">
                {isLoading && <p className="p-4">Loading onboardings...</p>}
                {!isLoading && onboardings.length === 0 && <p className="p-4 text-slate-500">No onboardings yet — click "Start onboarding".</p>}
                {onboardings.map(o => {
                    const pct = o.total_steps ? Math.round((o.done_steps / o.total_steps) * 100) : 0;
                    return (
                        <button key={o.onboarding_id} onClick={() => openDetail(o.onboarding_id)}
                                className="w-full text-left p-4 hover:bg-slate-50">
                            <div className="flex items-center justify-between mb-1">
                                <p className="font-medium text-slate-700">
                                    {o.person_name}
                                    <span className="ml-2 text-xs text-slate-400">{o.audience} · {o.template_name}</span>
                                </p>
                                <span className={`text-xs px-2 py-0.5 rounded ${o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-700'}`}>
                                    {o.status.replace('_', ' ')}
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 bg-slate-100 rounded-full h-2">
                                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-slate-500 w-20 text-right">{o.done_steps}/{o.total_steps} steps</span>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* The count that decides anything. 10 of 11 done with the ICA
                                    outstanding is not nearly finished, it is stopped. */}
                                {Number(o.blocking_open) > 0 && o.status === 'in_progress' && (
                                    <span className="text-xs font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">
                                        {o.blocking_open} blocking
                                    </span>
                                )}
                            </div>
                            {o.assigned_email && <p className="text-xs text-slate-400 mt-1">✉ {o.assigned_email}</p>}
                        </button>
                    );
                })}
            </div>

            {/* Start onboarding modal */}
            <Modal open={!!starting} onClose={() => setStarting(null)} title="Start an onboarding"
                   footer={<>
                       <button onClick={() => setStarting(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={handleStart}
                               disabled={isSaving || !starting?.template_id || !(String(selectedTemplate?.audience || '').startsWith('agent') ? starting?.agent_id : starting?.employee_id)}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                           {isSaving ? 'Starting...' : 'Start & send welcome email'}
                       </button>
                   </>}>
                {starting && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Checklist template</label>
                            <select value={starting.template_id}
                                    onChange={e => setStarting(prev => ({ ...prev, template_id: e.target.value, agent_id: '', employee_id: '' }))}
                                    className="mt-1 w-full border rounded-md px-3 py-2">
                                <option value="">Select...</option>
                                {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name} ({t.audience})</option>)}
                            </select>
                        </div>
                        {selectedTemplate && (
                            <div>
                                <label className="block text-sm font-medium text-slate-700">
                                    {String(selectedTemplate.audience || '').startsWith('agent') ? 'Agent' : 'Employee'}
                                </label>
                                <select value={String(selectedTemplate.audience || '').startsWith('agent') ? starting.agent_id : starting.employee_id}
                                        onChange={e => setStarting(prev => String(selectedTemplate.audience || '').startsWith('agent')
                                            ? { ...prev, agent_id: e.target.value } : { ...prev, employee_id: e.target.value })}
                                        className="mt-1 w-full border rounded-md px-3 py-2">
                                    <option value="">Select...</option>
                                    {(String(selectedTemplate.audience || '').startsWith('agent') ? agents : employees).map(p =>
                                        <option key={p.agent_id || p.employee_id} value={p.agent_id || p.employee_id}>
                                            {p.name || `${p.first_name} ${p.last_name}`}
                                        </option>)}
                                </select>
                                <p className="text-xs text-slate-500 mt-1">Add the person as an agent/employee first if they're not in the list.</p>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Personal email (welcome email goes here)</label>
                            <input value={starting.personal_email} type="email"
                                   onChange={e => setStarting(prev => ({ ...prev, personal_email: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" placeholder="their@personal-email.com" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Assigned brokerage email</label>
                            <input value={starting.assigned_email} type="email"
                                   onChange={e => setStarting(prev => ({ ...prev, assigned_email: e.target.value }))}
                                   className="mt-1 w-full border rounded-md px-3 py-2" placeholder="firstname@businessrealestatecorp.com" />
                            <p className="text-xs text-slate-500 mt-1">Create the mailbox in Microsoft 365 admin; recording it here puts it in the welcome email and the checklist.</p>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Onboarding detail modal */}
            <Modal open={!!detail} onClose={() => setDetail(null)} size="lg"
                   title={detail ? `${detail.person_name} — ${detail.template_name}` : ''}
                   footer={detail && detail.status === 'in_progress' ? (
                       <button onClick={cancelOnboarding} className="text-red-600 hover:underline mr-auto">Cancel this onboarding</button>
                   ) : null}>
                {detail && (
                    <div>
                        <div className="mb-4 text-sm text-slate-600 space-y-0.5">
                            {detail.assigned_email && <p><span className="font-medium">Brokerage email:</span> {detail.assigned_email}</p>}
                            {detail.personal_email && <p><span className="font-medium">Personal email:</span> {detail.personal_email}</p>}
                            <p><span className="font-medium">Started:</span> {new Date(detail.started_at).toLocaleDateString()}
                               {detail.completed_at && <> · <span className="font-medium">Completed:</span> {new Date(detail.completed_at).toLocaleDateString()} 🎉</>}</p>
                        </div>

                        {canProvision && detail.status === 'in_progress' && (
                            <div className="mb-4 border border-slate-200 rounded-md p-3 bg-slate-50">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                        <p className="text-sm font-medium text-slate-700">Microsoft 365 mailbox</p>
                                        <p className="text-xs text-slate-500">Creates the account in the tenant and assigns a license.</p>
                                    </div>
                                    <button onClick={openProvision}
                                            className="text-sm px-3 py-1.5 border border-slate-300 bg-white rounded-md hover:bg-slate-100">
                                        Create mailbox…
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Shown once, immediately after creation. The password is never stored. */}
                        {provisionResult && (
                            <div className="mb-4 border border-green-300 bg-green-50 rounded-md p-3">
                                <p className="text-sm font-medium text-green-800">{provisionResult.message}</p>
                                <dl className="mt-2 text-sm text-slate-700 space-y-1">
                                    <div><dt className="inline font-medium">Sign-in address: </dt>
                                         <dd className="inline">{provisionResult.userPrincipalName}</dd></div>
                                    <div><dt className="inline font-medium">Temporary password: </dt>
                                         <dd className="inline font-mono bg-white px-1.5 py-0.5 rounded border border-green-200">
                                             {provisionResult.temporaryPassword}</dd></div>
                                </dl>
                                <p className="text-xs text-green-800 mt-2">
                                    Write this down now — it is shown once and is not stored anywhere.
                                    They will be asked to change it at first sign-in.
                                </p>
                                {provisionResult.licenseError && (
                                    <p className="text-xs text-amber-800 mt-1">
                                        The account was created but the license failed: {provisionResult.licenseError}
                                    </p>
                                )}
                                <button onClick={() => setProvisionResult(null)}
                                        className="text-xs text-slate-500 hover:underline mt-2">Dismiss</button>
                            </div>
                        )}
                        {/* What is actually standing in the way, named, with the rule for
                            each. A count on its own tells the office manager nothing they
                            can act on. */}
                        {detail.can_complete === false && detail.blocking_open > 0 && (
                            <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3">
                                <p className="text-sm font-semibold text-amber-900">
                                    {detail.blocking_open} step{detail.blocking_open === 1 ? '' : 's'} must be
                                    settled before {detail.person_name} can start
                                </p>
                                <ul className="mt-2 space-y-1.5">
                                    {(detail.blocking || []).map((b, i) => (
                                        <li key={i} className="text-xs text-amber-900">
                                            <span className="font-medium">{b.title}</span>
                                            {b.authority && <span className="block text-amber-700">{b.authority}</span>}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-xs text-amber-700 mt-2">
                                    Each can be completed, or set aside with a stated reason.
                                </p>
                            </div>
                        )}
                        <div className="divide-y border rounded-md">
                            {detail.steps.map(s => (
                                <div key={s.onboarding_step_id}
                                     className={`p-3 flex items-start gap-3 ${
                                         s.is_blocking && s.status === 'pending' ? 'bg-amber-50/50' : ''}`}>
                                    <input type="checkbox" checked={s.status === 'done'} className="mt-1"
                                           onChange={() => setStep(s, s.status === 'done' ? 'pending' : 'done')} />
                                    <div className="flex-1 min-w-0">
                                        <p className={`font-medium ${s.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                            {s.title}
                                            {!!s.is_blocking && (
                                                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider
                                                                 text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded align-middle">
                                                    blocks
                                                </span>
                                            )}
                                        </p>
                                        {s.description && <p className="text-xs text-slate-500">{s.description}</p>}
                                        {/* Why the step exists, in front of the person deciding
                                            whether to skip it — not in a migration comment. */}
                                        {s.authority && <p className="text-xs text-slate-400 mt-0.5">{s.authority}</p>}
                                        {s.due_on && s.status === 'pending' && (
                                            <p className="text-xs text-amber-700 mt-0.5">
                                                Due {new Date(s.due_on).toLocaleDateString()}
                                            </p>
                                        )}
                                        {s.skipped_reason && (
                                            <p className="text-xs text-amber-700 mt-0.5">Set aside: {s.skipped_reason}</p>
                                        )}
                                        {s.notes && <p className="text-xs text-blue-600 mt-0.5">{s.notes}</p>}
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded ${STEP_BADGE[s.status]}`}>{s.status}</span>
                                    {s.status === 'pending' && (
                                        <button onClick={() => setStep(s, 'skipped')} className="text-xs text-slate-400 hover:underline">skip</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Setting aside a step that blocks */}
            <Modal open={!!skipping} onClose={() => setSkipping(null)} size="md"
                   title="Set aside a step that blocks"
                   footer={
                       <>
                           <button onClick={() => setSkipping(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
                           <button onClick={() => setStep(skipping.step, 'skipped', skipping.reason.trim())}
                                   disabled={!skipping?.reason?.trim()}
                                   className="px-4 py-2 text-sm bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:bg-rose-300">
                               Set aside
                           </button>
                       </>
                   }>
                {skipping && (
                    <div>
                        <p className="font-medium text-slate-700">{skipping.step.title}</p>
                        {skipping.step.authority && (
                            <p className="text-xs text-slate-500 mt-1">{skipping.step.authority}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-3">
                            This step blocks {detail?.person_name} starting. Say why it is being set aside —
                            without a reason there is no way to tell later whether it was a decision or an
                            oversight, and this is the record an examiner reads.
                        </p>
                        <textarea rows={3} value={skipping.reason}
                                  onChange={e => setSkipping({ ...skipping, reason: e.target.value })}
                                  placeholder="e.g. Licence confirmed by phone with IDFPR; sponsor card to follow"
                                  className="mt-3 w-full p-2 border border-slate-300 rounded-md text-sm
                                             focus:outline-none focus:ring-2 focus:ring-rose-500" />
                    </div>
                )}
            </Modal>

            {/* Create a Microsoft 365 mailbox */}
            <Modal open={!!provision} onClose={() => setProvision(null)} size="md"
                   title="Create a Microsoft 365 mailbox"
                   footer={provisionState?.available ? (
                       <>
                           <button onClick={() => setProvision(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
                           <button onClick={submitProvision} disabled={isSaving || !provision?.userPrincipalName?.trim()}
                                   className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300">
                               {isSaving ? 'Creating…' : 'Create account'}
                           </button>
                       </>
                   ) : (
                       <button onClick={() => setProvision(null)} className="px-4 py-2 text-sm text-slate-600">Close</button>
                   )}>
                {provision && !provisionState && <p className="text-sm text-slate-500">Checking the tenant…</p>}

                {provision && provisionState && !provisionState.available && (
                    <div>
                        <p className="text-sm text-slate-700 mb-2">Mailboxes cannot be created from here yet.</p>
                        <p className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
                            {provisionState.reason}
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            Until then, create the mailbox in the Microsoft 365 admin center and record the
                            address on the "assign email" step.
                        </p>
                    </div>
                )}

                {provision && provisionState?.available && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Email address</label>
                            <input value={provision.userPrincipalName}
                                   onChange={e => setProvision({ ...provision, userPrincipalName: e.target.value })}
                                   className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                                   placeholder="first.last@businessrealestatecorp.com" />
                            <p className="text-xs text-slate-500 mt-1">This becomes their sign-in name and mailbox.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">License</label>
                            <select value={provision.skuId}
                                    onChange={e => setProvision({ ...provision, skuId: e.target.value })}
                                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm">
                                <option value="">No license (account only — cannot receive mail)</option>
                                {provisionState.skus.map(s => (
                                    <option key={s.skuId} value={s.skuId} disabled={!s.free}>
                                        {s.skuPartNumber} — {s.free} of {s.total} free
                                        {s.hasExchange ? '' : ' (no mailbox)'}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-500 mt-1">
                                Assigning a license consumes a paid seat. A license without Exchange creates an
                                account that cannot receive mail.
                            </p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default OnboardingAdminPage;
